import { useMemo } from 'react'
import { useQuery, UseQueryOptions } from 'react-query'
import sortedUniq from 'lodash/sortedUniq'
import { isNilOrUndef } from 'utils/isNilOrUndef'
import { NftDetails, MoleMeta } from 'views/VvsMinerMoleMyMoles/types'
import { QueryKey } from 'config/constants/queryKey'

export const INFO_API_ENDPOINT = process.env.REACT_APP_INFO_API_ENDPOINT
/**
 *  Get single Mole
 *
 *  ex: ENDPOINT/mole/metas/1
 */
export const GET_MOLE = `mole/metas`
/**
 *  Get Moles
 *
 *  ex: ENDPOINT/mole/metas/ids?id=1&id=2
 */
export const GET_MOLES = `mole/metas/ids`

export const fetchMole = async (id: string): Promise<MoleMeta> => {
  const res = await fetch(`${INFO_API_ENDPOINT}/${GET_MOLE}/${id}`)
  return res.json()
}

export const fetchMoles = async (...ids: string[]): Promise<MoleMeta[]> => {
  const queryIdsString = ids.map((id) => `id=${id}`).join('&')
  const res = await fetch(`${INFO_API_ENDPOINT}/${GET_MOLES}?${queryIdsString}`)
  return res.json()
}

export const useFetchMyMoles = (
  molesIds: string[],
  queryOptions?: Partial<Pick<UseQueryOptions, 'enabled'>>,
): [NftDetails[], boolean] => {
  const moleIdsReady = !isNilOrUndef(molesIds)
  const fetchingIds = sortedUniq(molesIds?.sort((a, b) => parseInt(a) - parseInt(b)))
  const fetchingIdsLength = fetchingIds.length

  const {
    data: molesDetail,
    isFetching: isMolesFetching,
    isError,
  } = useQuery([QueryKey.MY_MOLE_META_DETAILS, fetchingIds], () => fetchMoles(...fetchingIds), {
    enabled: moleIdsReady && fetchingIdsLength > 0 && (queryOptions?.enabled ?? true),
    ...queryOptions,
  })

  const myMoles: NftDetails[] | null = useMemo(() => {
    if (isMolesFetching || !moleIdsReady) {
      return null
    }
    if (isError || fetchingIdsLength === 0) {
      return []
    }
    return molesDetail?.map((mole) => {
      return {
        id: mole.id,
        imgUrl: mole.image,
        name: mole.name,
        rank: mole.rank,
        attributes: mole.attributes,
        rarity: mole.rarity,
      }
    })
  }, [molesDetail, isError, fetchingIdsLength, isMolesFetching, moleIdsReady])

  return [myMoles, isMolesFetching]
}
